import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Check, Shuffle, ArrowLeftRight } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { firstName } from '../../lib/play';
import ScoreEntry from './ScoreEntry';
import CardActions from './CardActions';
import StandingsCard from './StandingsCard';

export default function PlanView({ data, ratingOf, sessionId, onLogged, onReshuffle }) {
  const matchById = {};
  data.matches.forEach((m) => { matchById[m.id] = m; });
  const nm = (id) => firstName(ratingOf[id]?.name || 'Guest');
  const squads = data.plan.mode === 'squad' ? data.plan.squads : null;
  const score = data.squad_score;

  return (
    <div className="space-y-4" data-testid="plan-view">
      {squads && (
        <div className="rounded-2xl border p-3 space-y-2" data-testid="squad-scoreboard">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-rose-500" /> Red
              </p>
              <p className="text-xs text-muted-foreground truncate">{(squads.a || []).map(nm).join(', ')}</p>
            </div>
            <p className="font-display font-black text-2xl tabular-nums" data-testid="squad-score">
              {score ? `${score.a}–${score.b}` : '0–0'}
            </p>
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
                Blue <span className="w-2 h-2 rounded-full bg-sky-500" />
              </p>
              <p className="text-xs text-muted-foreground truncate">{(squads.b || []).map(nm).join(', ')}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            {score?.played
              ? (score.leader ? `${score.leader === 'a' ? 'Red' : 'Blue'} lead the tie · ${score.played} match${score.played > 1 ? 'es' : ''} in` : `All square after ${score.played} match${score.played > 1 ? 'es' : ''}`)
              : 'Squad tie — log scores to move the tie along'}
            {score?.format === '3v3' ? ' · 3v3, best of 3' : ''}
          </p>
          {data.can_score && score?.complete && (
            <TieBreaker squads={squads} score={score} nm={nm} sessionId={sessionId} onDone={() => onLogged([])} />
          )}
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {data.plan.rounds.length} round{data.plan.rounds.length > 1 ? 's' : ''}{data.plan.progressive ? ' so far' : ''} · ~{data.plan.match_minutes} min each · {data.plan.player_ids.length} players
          {data.plan.points_to_win ? ` · games to ${data.plan.points_to_win}` : ''}
        </p>
        {data.can_score && (
          <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={onReshuffle} data-testid="plan-reshuffle-btn">
            <Shuffle className="w-3.5 h-3.5 mr-1" /> Re-shuffle
          </Button>
        )}
      </div>
      {data.standings && (
        <StandingsCard standings={data.standings} nm={nm} sessionId={sessionId}
          canScore={data.can_score} onAdvanced={() => onLogged([])} />
      )}
      {data.plan.pairs_warning && (
        <p className="rounded-xl bg-amber-500/10 border border-amber-500/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="plan-pairs-warning">
          {data.plan.pairs_warning}
        </p>
      )}
      {data.plan.rest_warning && (
        <p className="rounded-xl bg-amber-500/10 border border-amber-500/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="plan-rest-warning">
          {data.plan.rest_warning}
        </p>
      )}
      {data.plan.repeat_warning && (
        <p className="rounded-xl bg-amber-500/10 border border-amber-500/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="plan-repeat-warning">
          {data.plan.repeat_warning}
        </p>
      )}
      {data.plan.load && Object.keys(data.plan.load).length > 0 && (
        <div className="rounded-2xl border p-3" data-testid="plan-load">
          <p className="font-display text-xs font-bold uppercase tracking-[0.16em] mb-2">
            Game load{data.plan.max_consecutive ? ` · max ${data.plan.max_consecutive} in a row` : ''}
            {data.plan.no_repeat ? ' · no repeat pairs' : ''}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.plan.player_ids.map((id) => {
              const l = data.plan.load[id] || {};
              return (
                <span key={id} data-testid={`load-chip-${id}`}
                  className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-xs font-semibold tabular-nums">
                  {nm(id)}
                  <span className="text-muted-foreground">
                    {l.played ?? 0} play · {l.rest ?? 0} rest{l.partners ? ` · ${l.partners} partner${l.partners > 1 ? 's' : ''}` : ''}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-dashed p-3" data-testid="schedule-card-box">
        <p className="text-xs font-semibold mb-2">Schedule image &amp; branded PDF</p>
        <CardActions
          buildPath={`/sessions/${sessionId}/play/cards/schedule`}
          sendPath={`/sessions/${sessionId}/play/cards/schedule/send`}
          canSend={data.can_score}
          filename={`schedule-${data.session.date}.png`}
          shareText={`${data.session.title} · court schedule`}
          sentInfo={data.plan.card_sent}
          pdf={{
            buildPath: `/sessions/${sessionId}/play/cards/schedule-pdf`,
            sendPath: `/sessions/${sessionId}/play/cards/schedule-pdf/send`,
            filename: `schedule-${data.session.date}.pdf`,
          }}
        />
      </div>
      {data.plan.rounds.map((r) => (
        <div key={r.round} data-testid={`plan-round-${r.round}`}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="font-display text-xs font-bold uppercase tracking-[0.16em]">
              {r.stage_label || (r.decider ? 'Final' : `Round ${r.round}`)}
            </p>
            {r.sit_out.length > 0 && (
              <p className="text-xs text-muted-foreground truncate max-w-[60%]">Resting: {r.sit_out.map(nm).join(', ')}</p>
            )}
          </div>
          {!!r.byes?.length && (
            <p className="text-xs text-muted-foreground mb-1.5" data-testid={`round-byes-${r.round}`}>
              Bye to {r.byes.map((p) => p.map(nm).join(' & ')).join(', ')}
            </p>
          )}
          {data.can_score && !r.courts.some((c) => c.logged_match_id) && (
            <SwapBar round={r} nm={nm} sessionId={sessionId} onDone={() => onLogged([])} />
          )}
          <div className="space-y-2">
            {r.courts.map((c) => (
              <CourtCard key={c.court} c={c} round={r.round} nm={nm} logged={c.logged_match_id ? matchById[c.logged_match_id] : null}
                canScore={data.can_score} sessionId={sessionId} pointsToWin={data.plan.points_to_win || data.settings.points_to_win} onLogged={onLogged}
                squadLabels={squads ? ['Red', 'Blue'] : null} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TieBreaker({ squads, score, nm, sessionId, onDone }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pairA, setPairA] = useState([]);
  const [pairB, setPairB] = useState([]);

  const level = !score.leader;
  const combos = (ids) => {
    const out = [];
    for (let i = 0; i < ids.length; i += 1) for (let j = i + 1; j < ids.length; j += 1) out.push([ids[i], ids[j]]);
    return out;
  };
  const key = (p) => p.join('|');

  const post = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(`/sessions/${sessionId}/squad/${path}`, body);
      toast.success(msg);
      setPicking(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not extend the tie');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-dashed p-3 space-y-2" data-testid="tie-breaker">
      <p className="text-xs font-bold">
        {level
          ? `Tie level at ${score.a}–${score.b}. Play it out?`
          : `${score.leader === 'a' ? 'Red' : 'Blue'} lead ${Math.max(score.a, score.b)}–${Math.min(score.a, score.b)}. Keep going?`}
      </p>
      {!picking ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" disabled={busy}
            onClick={() => post('extra-rounds', { rounds: 1 }, 'One more round added')} data-testid="tie-more-rounds-btn">
            Add one more round
          </Button>
          <Button size="sm" className="rounded-full h-8 text-xs" disabled={busy}
            onClick={() => setPicking(true)} data-testid="tie-final-btn">
            Play a final
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="tie-final-picker">
          {[['Red', squads.a || [], pairA, setPairA, 'a'], ['Blue', squads.b || [], pairB, setPairB, 'b']].map(
            ([label, ids, val, set, side]) => (
              <div key={label}>
                <p className="text-xs font-semibold mb-1">{label} pair</p>
                <div className="flex flex-wrap gap-1.5">
                  {combos(ids).map((p) => (
                    <button key={key(p)} type="button" onClick={() => set(p)} data-testid={`tie-pair-${side}-${key(p)}`}
                      className={`px-2.5 h-8 rounded-full border-2 text-xs font-bold transition-colors ${
                        key(val) === key(p) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                      }`}>
                      {p.map(nm).join(' & ')}
                    </button>
                  ))}
                </div>
              </div>
            ),
          )}
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs flex-1" onClick={() => setPicking(false)} data-testid="tie-final-cancel">
              Cancel
            </Button>
            <Button size="sm" className="rounded-full h-8 text-xs flex-1" disabled={busy || pairA.length !== 2 || pairB.length !== 2}
              onClick={() => post('decider', { pair_a: pairA, pair_b: pairB }, 'Final added — winner takes the tie')}
              data-testid="tie-final-confirm">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add the final'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SwapBar({ round, nm, sessionId, onDone }) {
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(null);
  const [busy, setBusy] = useState(false);

  const players = [
    ...round.courts.flatMap((c) => [...c.team_a, ...c.team_b]),
    ...(round.sit_out || []),
  ];

  const pick = async (id) => {
    if (!first) { setFirst(id); return; }
    if (first === id) { setFirst(null); return; }
    setBusy(true);
    try {
      await api.post(`/sessions/${sessionId}/plan/swap`, { round: round.round, user_a: first, user_b: id });
      toast.success(`Swapped ${nm(first)} and ${nm(id)}`);
      setFirst(null);
      setEditing(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not swap');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs mb-1.5 -ml-2"
        onClick={() => setEditing(true)} data-testid={`swap-open-${round.round}`}>
        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Swap players
      </Button>
    );
  }

  return (
    <div className="rounded-xl border border-dashed p-2.5 mb-2 space-y-2" data-testid={`swap-bar-${round.round}`}>
      <p className="text-xs text-muted-foreground">
        {first ? `Now tap who ${nm(first)} should change places with` : 'Tap the player you want to move'}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {players.map((id) => (
          <button key={id} type="button" disabled={busy} onClick={() => pick(id)}
            data-testid={`swap-pick-${round.round}-${id}`}
            className={`h-7 px-2.5 rounded-full border text-xs font-semibold transition-colors ${first === id ? 'bg-amber-500 text-black border-amber-500' : 'hover:bg-muted'}`}>
            {nm(id)}
          </button>
        ))}
      </div>
      <Button size="sm" variant="outline" className="rounded-full h-7 text-xs w-full"
        onClick={() => { setEditing(false); setFirst(null); }} data-testid={`swap-cancel-${round.round}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Done'}
      </Button>
    </div>
  );
}

function CourtCard({ c, round, nm, logged, canScore, sessionId, pointsToWin, onLogged, squadLabels }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (scoreA, scoreB) => {
    setBusy(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/matches`, {
        team_a: c.team_a, team_b: c.team_b, score_a: scoreA, score_b: scoreB, round, court: c.court,
      });
      toast.success('Match logged');
      setOpen(false);
      onLogged(r.data.events);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log match');
    } finally {
      setBusy(false);
    }
  };

  const aWon = logged ? logged.score_a > logged.score_b : null;
  return (
    <div className={`rounded-2xl border p-3 ${logged ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-card'}`} data-testid={`court-card-${round}-${c.court}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
        <span>Court {c.court}{squadLabels ? ` · ${squadLabels[0]} vs ${squadLabels[1]}` : ''}</span>
        <span>Balance {c.balance_pct}% · A {c.win_prob_a}% fav</span>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <TeamBlock names={c.team_a.map(nm)} rating={c.rating_a} won={aWon === true} lost={aWon === false} testId="team-a" />
        <div className="text-center">
          {logged ? (
            <p className="font-display font-black text-xl tabular-nums" data-testid="court-score">{logged.score_a}–{logged.score_b}</p>
          ) : <p className="font-display font-black text-muted-foreground">vs</p>}
        </div>
        <TeamBlock names={c.team_b.map(nm)} rating={c.rating_b} won={aWon === false} lost={aWon === true} right testId="team-b" />
      </div>
      <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-amber-500" style={{ width: `${c.win_prob_a}%` }} />
      </div>
      {logged ? (
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Logged</p>
      ) : canScore && (
        open ? (
          <ScoreEntry pointsToWin={pointsToWin} busy={busy} onSubmit={submit} onCancel={() => setOpen(false)} nameA={c.team_a.map(nm).join(' & ')} nameB={c.team_b.map(nm).join(' & ')} />
        ) : (
          <Button size="sm" className="rounded-full mt-2 w-full h-9" onClick={() => setOpen(true)} data-testid={`log-score-${round}-${c.court}`}>Log score</Button>
        )
      )}
    </div>
  );
}

function TeamBlock({ names, rating, won, lost, right, testId }) {
  return (
    <div className={`${right ? 'text-right' : ''} ${lost ? 'opacity-50' : ''}`} data-testid={testId}>
      {names.map((n) => <p key={n} className={`text-sm truncate ${won ? 'font-black' : 'font-semibold'}`}>{n}</p>)}
      <p className="text-xs text-muted-foreground tabular-nums">avg {rating}</p>
    </div>
  );
}
